<script setup>
  import { ref, reactive, computed, watchEffect } from 'vue';
  import { useIntersectionObserver, useMutationObserver } from '@vueuse/core';

  const props = defineProps({
    contentContainer: {
      type: Object,
    },
    style: {
      type: String,
      default: 'block'
    }
  })

  const tocRoot = ref(null);
  const tocElement = ref(null);
  const tocDimensions = ref({ width: 0, height: 0});
  const sections = ref([]);
  const selectedSection = ref(null);
  const selectedLinkIndicator = reactive({ width: 0, position: 0 });
  const markerTop = ref(null);
  const markerTopVisible = ref(false);
  const markerBottom = ref(null);
  const markerBottomVisible = ref(false);
  const userClicked = ref(false);
  const markerTopPosition = ref(0);
  const scrollDirection = ref('down');
  const intialLoad = ref(true);
  const observers = ref([]);

  const selectedLinkIndicatorStyles = computed(() => ({
    '--jwx-toc-selected-indicator-width': `${selectedLinkIndicator.width}px`,
    '--jwx-toc-selected-indicator-position': `${selectedLinkIndicator.position}px`,
  }))  

  const initObserver = (element, observerPosition = 'top') => {
    const thresholdCount = 20;
    const threshold = Array.from({ length: thresholdCount }).map((_, i) => i / thresholdCount);
    const topObserverOptions = { rootMargin: `-${tocDimensions.value.height}px 0px -55% 0px`, threshold }    
    const bottomObserverOptions = { rootMargin: `-55% 0px 0px 0px`, threshold } 
    const options = observerPosition === 'top' ? topObserverOptions : bottomObserverOptions;  

    const observer = useIntersectionObserver(
      element,
      ([entry]) => {
        setScrollDirection();

        if (userClicked.value) return;

        if (intialLoad.value) {
          selectSection(sections.value[0]);

          if (entry.time > 3500) intialLoad.value = false;

          return;
        }

        if (element.dataset.tocMarker === 'top') {
          markerTopVisible.value = entry.isIntersecting;

        }
        
        else if (element.dataset.tocMarker === 'bottom') {
          markerBottomVisible.value = entry.isIntersecting;
        }
        
        if (element.dataset.tocMarker || markerTopVisible.value || markerBottomVisible.value) return;

        if (entry.isIntersecting) {
          const section = sections.value.find((section) => section.id === entry.target.id);
          
          if (scrollDirection.value === 'down') {
            // If the top most section is not fully visible, select the next section
            if (entry.boundingClientRect.top < 0 && entry.intersectionRatio < 0.2) {
              const sectionIndex = sections.value.findIndex((section) => section.id === entry.target.id);
              const nextSection = sections.value[sectionIndex + 1];

              selectSection(nextSection);

              return;
            }

            if (entry.boundingClientRect.top > -200 && entry.boundingClientRect.top < 200) {
              selectSection(section);

              return;
            }
          }
          
          if (scrollDirection.value === 'up' && entry.intersectionRatio > 0.2) {
            selectSection(section);

            return;
          }
        }
      },
      options
    )

    observers.value.push(observer);
  }
  
  const deselectAllSections = () => {
    sections.value.forEach((section) => {
      section.isSelected = false;
    })
  }

  const selectSection = (section) => {
    if (!section) return;

    deselectAllSections();
    selectedSection.value = section;
    section.isSelected = true;
  }

  const setScrollDirection = () => {
    const currentMarkerTopPosition = markerTop.value.getBoundingClientRect().top;
    const markerTopPositionDelta = markerTopPosition.value - currentMarkerTopPosition;

    scrollDirection.value = 
      markerTopPositionDelta > 0 
      ? 'down' 
      : markerTopPositionDelta < 0
      ? 'up'
      : scrollDirection.value;
    markerTopPosition.value = currentMarkerTopPosition;    
  }

  const getSections = () => {
    if (!tocRoot.value) return;
    
    const headerElements = Array.from(tocRoot.value.querySelectorAll('header > h2, [data-toc]'));

    if (!headerElements) return;

    for (const header of headerElements) {
      const section = header.closest('section, [data-toc]');

      if (!section) continue;

      section.id = section.id || `section-${header.textContent.toLowerCase().replace(/\s/g, '-')}`;

      sections.value.push({
        element: section,
        title: header.textContent,
        id: section.id,
        isSelected: false
      });
    }
  }

  const createMarkers = () => {
    if (!tocRoot.value) return;

    markerTop.value = document.createElement('span');
    markerBottom.value = document.createElement('span');

    markerTop.value.classList.add('jwx-toc-marker', 'jwx-toc-marker--top');
    markerBottom.value.classList.add('jwx-toc-marker', 'jwx-toc-marker--bottom');

    markerTop.value.dataset.tocMarker = 'top';
    markerBottom.value.dataset.tocMarker = 'bottom';

    tocRoot.value.appendChild(markerTop.value);
    tocRoot.value.appendChild(markerBottom.value);
  }

  const scrollToTOCLink = (id) => {
    const tocLink = tocElement.value.querySelector(`#toc-link-${id}`);

    if (!tocLink) return;
    
    const tocLinkCenterPoint = tocLink.offsetLeft + tocLink.offsetWidth / 2;
    const tocElementCenterPoint = tocElement.value.offsetWidth / 2;
    const centeringDistance = tocElementCenterPoint - tocLinkCenterPoint;
    const absoluteCenteringDistance = centeringDistance < 0 ? Math.abs(centeringDistance) : 0;
    
    tocElement.value.scrollTo({
      top: 0,
      left: absoluteCenteringDistance,
      behavior: 'smooth'
    });

    calculateSelectedLinkIndicatorValues(tocLink);
  }

  const calculateSelectedLinkIndicatorValues = (link) => {
    selectedLinkIndicator.width = link.offsetWidth;
    selectedLinkIndicator.position = link.offsetLeft;
  }

  const tocLinkClickHanlder = (event) => {
    const targetId = event.target.getAttribute('href').replace('#', '');
    const section = sections.value.find((section) => section.id === targetId);

    deselectAllSections();
    scrollToTOCLink(targetId);

    section.isSelected = true;
    userClicked.value = true;

    setTimeout(() => {
      userClicked.value = false;
    }, 1000);
  }

  const initTableOfContents = () => {
      const tocMarkerParent = props.contentContainer ? tocRoot.value : tocElement.value.parentElement;
      tocDimensions.value = tocElement.value.getBoundingClientRect();
      
      tocMarkerParent.style.position = 'relative';

      // Used for table-of-contents styling
      document.documentElement.setAttribute('data-toc', 'true');
      document.documentElement.style.setProperty('--toc-height', `${tocDimensions.value.height}px`);

      if (window.location.hash) {
        intialLoad.value = false;

        if (!sections.value) return;

        const jumpSection = sections.value.find((section) => section.id === window.location.hash?.replace('#', ''));

        jumpSection?.element.scrollIntoView();
      }      

      if (!sections.value) return; 
  
      sections.value.forEach((section) => initObserver(section.element));
      initObserver(markerTop.value, 'top');
      initObserver(markerBottom.value, 'bottom');
  }

  const rebuild = () => {
    const tocLink = tocElement.value.querySelector(`[data-toc-selected="true"]`);

    sections.value = [];
    observers.value.forEach((observer) => observer.stop());
    observers.value = [];

    getSections();
    initTableOfContents();

    if (!tocLink) return;

    calculateSelectedLinkIndicatorValues(tocLink);
  }

  useMutationObserver(tocRoot, () => {
    rebuild();
  }, {
    childList: true,
  });

  watchEffect(() => {
    if (markerTopVisible.value || markerBottomVisible.value) {
      deselectAllSections();
    }

    if (markerTopVisible.value) {
      selectSection(sections.value[0]);
    }

    if (markerBottomVisible.value) {
      selectSection(sections.value[sections.value.length - 1]);
    }
  })

  watchEffect(() => {
    if (selectedSection.value) {
      scrollToTOCLink(selectedSection.value.id);
    }
  })

  watchEffect(() => {
    if (!props.contentContainer || tocRoot.value) return;

    tocRoot.value = props.contentContainer;

    if (!tocRoot.value) return;
    
    getSections();
    createMarkers();
    
    setTimeout(() => {
      initTableOfContents();
    }, 100);    
  })

  defineExpose({
    rebuild
  });  
</script>

<template>
  <div
    ref="tocElement"
    class="jwx-toc"
    :style="selectedLinkIndicatorStyles"
    data-position="bottom"
    :data-transparent="props.style === 'floatingPills'"
    :data-indicator="props.style !== 'floatingPills'"
  >
    <template v-if="sections.length > 1">
      <a
        v-for="section in sections"
        @click="tocLinkClickHanlder"
        :key="section.id" 
        :id="`toc-link-${section.id}`"
        :href="`#${section.id}`" 
        class="jwx-toc__link" 
        :data-toc-selected="section.isSelected"
        :data-shape="props.style === 'floatingPills' ? 'pill' : null"
      >
        {{ section.title }}
      </a>
    </template>
  </div>
</template>